import type { FieldValues, Path, PathValue } from "react-hook-form";

import { formatPartialPhoneNumber } from "@homeinstead/shared";
import { get, useFormContext } from "react-hook-form";
import { FormError } from "./FormError";
import { Input } from "./Input";

// The type ensures that you can only pass a fieldName that ends up at a string property in T
// – if you tried to pass a path that leads to a number or boolean, TypeScript would reject it.
// Note: PathValue comes from react-hook-form
type StringFieldName<T extends FieldValues> = {
  [K in Path<T>]: PathValue<T, K> extends string ? K : never;
}[Path<T>];

export interface PhoneInputProps<T extends FieldValues> {
  /**
   * `fieldName` must be a valid Path<T> pointing to a string property
   */
  fieldName: StringFieldName<T>;
  className?: string;
  disabled?: boolean;
  errorClassName?: string;
  /** When true, show validation error after a submit attempt (touched or submitted). Default false. */
  showErrorAfterSubmit?: boolean;
}

// This component is a wrapper around our Input component but adds utilities for phone number formatting
// and integrates into react-hook-form for validation and state management.
export function PhoneInput<T extends FieldValues>({
  fieldName,
  className,
  disabled,
  errorClassName,
  showErrorAfterSubmit = false,
}: PhoneInputProps<T>) {
  const {
    register,
    setValue,
    formState: { errors, touchedFields, isSubmitted },
  } = useFormContext<T>();

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPartialPhoneNumber({
      franchisePhone: e.target.value,
    });
    setValue(fieldName, formattedValue as PathValue<T, typeof fieldName>, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // Use get to access nested properties
  const phoneError = get(errors, fieldName);
  const phoneTouched = get(touchedFields, fieldName);
  const showError =
    (phoneTouched || (showErrorAfterSubmit && isSubmitted)) &&
    phoneError?.message;

  return (
    <div>
      <Input
        disabled={disabled}
        placeholder="Phone Number"
        type="tel"
        {...register(fieldName, {
          required: "Phone number is required",
          validate: {
            validFormat: (value: string) =>
              !value ||
              value.replace(/\D/g, "").length === 10 ||
              "Please enter a valid 10-digit phone number",
          },
        })}
        className={className}
        onChange={handlePhoneChange}
      />

      {showError && (
        <FormError.span className={errorClassName}>
          {String(phoneError.message)}
        </FormError.span>
      )}
    </div>
  );
}
